import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import useBuildingTypes from "@/dataProviders/BuildingTypes/providers/BuildingTypesProvider";

import { buildingTypeDisplayLabel } from "../utils/buildingTypeLabel";

import type { NewBuildingProjectType } from "../types/NewBuildingProjectType";
import { replaceProjectBuildingAreas } from "./projectBuildingAreaWrites";
import { buildingUsesToSave, hasAnyBuildingArea, type ProjectBuildingAreasWriteInput } from "./buildingAreaWriteInput";
import { upsertProjectCalculation, upsertProjectLocation } from "./projectSetupWrites";
import { isRailUnavailable, noteRailUnavailable } from "./projectWriteAvailability";
import { ProjectWriteUnavailableError } from "./projectWriteErrors";

/**
 * Persists what the create wizard's Project step owns, onto the base project the start dialog
 * created (RTLCA-5404): the address into project_location, the calculation setup (type, period,
 * threshold, stages) into project_calculation, and the building areas — GFA, floor counts, heated
 * areas — into project_building_area (RTLCA-5781), each with its own trail row. Project scalars
 * (name, building type, years) have no user-role update permission yet, so edits to them inside the
 * wizard are carried by the in-memory project until that rail lands.
 *
 * The upserts key on (project_id, revision_id) — the building areas on
 * (project_id, building_area_type, revision_id) — so re-leaving the step overwrites the same rows;
 * `firstSave` only decides the trail wording ("set" vs "updated").
 */
export const saveProjectDetailsStep = async (
  project: NewBuildingProjectType,
  projectId: string,
  firstSave: boolean
): Promise<void> => {
  const operation = firstSave ? "create" : "update";
  const address = project.buildingAddress;
  const { calculationType: calculation, emissionMetaData, referencedAreas, heatArea } = project.emissionCalculationAssumptions;
  const includedPhases = emissionMetaData.includedPhases;

  // Sequential, not Promise.all (RTLCA-5537). Each of these is a bundled …WithVersionMutation, so each
  // inserts a trail row whose version number is stamped in INSERT order by a BEFORE INSERT trigger,
  // while its created_at is transaction start. Run concurrently over the un-batched HttpLink, the two
  // overlapping transactions could take their numbers in the opposite order to their timestamps and
  // leave the History page rendering the project's first changes inverted. One extra round trip on the
  // wizard's Project step buys a trail that reads in the order the changes happened.
  //
  // Consequence to know: a failed address write now means the calculation write is never attempted,
  // where before it would already have been in flight. A throw here is already treated as incomplete
  // setup rather than a failed create (CreateBuildingProjectService, RTLCA-5524).
  await upsertProjectLocation(
    {
      projectId,
      street: address.street,
      streetNumber: address.streetNumber,
      postalCode: address.postalCode,
      city: address.city,
      country: address.country,
    },
    // No baseline: this step is the project's first address write, so every field it fills in is new.
    { operation, previous: null }
  );

  // The start dialog's canStartProject guarantees these before create; the guard is for the
  // template's unset defaults (calculationTypeEnum null, considerationPeriod null) so a
  // half-initialised project can never write a calculation row pointing at no calculation type.
  // `!= null`, NOT truthiness: Denmark's Custom calculation type is id 0, and the truthiness check
  // silently skipped this write for it — the project then had no project_calculation row at all and
  // the backend refused to compose it (RTLCA-5595).
  if (calculation.calculationTypeEnum != null && calculation.considerationPeriod != null) {
    const calculationTypeLabel = useCalculationTypes().getSelectedCalculationType(calculation.calculationTypeEnum)?.label ?? "";

    await upsertProjectCalculation(
      {
        projectId,
        calculationTypeId: calculation.calculationTypeEnum,
        considerationPeriod: calculation.considerationPeriod,
        // The project keeps null for "no limit value known yet" — CalculationTypeForm writes it when
        // no per-building-type row matches — but the column cannot hold one, so the coercion belongs
        // here, at the write, rather than in the store where it would erase that distinction.
        //
        // Most calculation types reach this line with null. A type carries a flat `threshold` only in
        // Denmark, the Faroe Islands and Germany's QNG-PLUS/PREMIUM, and per-building-type rows only
        // in Denmark's 2025 types and Finland's climate declarations; every other type — HMS, TEK17,
        // SBTi, all fourteen Level(s), Klimatdeklaration, QNG, WLC — carries neither, and the start
        // dialog does not ask for one. Sent as null, each of them failed the whole create with
        // "The project could not be created", after the base project had already been written.
        thresholdLimit: calculation.thresholdLimitWithoutExtra ?? 0,
        installationThresholdLimit: calculation.installationThresholdLimit ?? null,
        useDefaultEmissions: calculation.useDefaultEmissions,
        stageIds: includedPhases,
      },
      { operation, entityLabel: calculationTypeLabel }
    );
  }

  // The building areas, last and sequentially, for the same trail-ordering reason as the two above
  // (RTLCA-5537). Both rows travel together whenever they travel at all, because the columns are NOT
  // NULL and the wrapper reads each type back by name, so a project that acquired only one of them
  // would read `null` for the other and be indistinguishable from one with no rows at all.
  //
  // But only when the reader has answered SOMETHING (RTLCA-5784). The start dialog does not ask for
  // areas, so it reaches this line with all six unset — and writing them then does not record "not
  // answered", which the columns cannot hold; it records a building of 0 m² with 0 floors, none of it
  // heated. That is a number nobody typed, it validates (`isNonNegative(0)`), and the settings page
  // opens showing it. No rows says what is actually true, and is the state the wrapper already reads
  // as null and the card already renders as six empty fields.
  //
  // No baseline to diff against — this is the step that first writes them — so the trail row lists
  // every field the reader answered.
  //
  // A mixed-use project's uses travel in the same rows, a pair per use; a single use is
  // one pair under the project's building type.
  //
  // NOT on the address and calculation rows' terms, though, and that is deliberate. Those two throw
  // because the backend refuses to compose a project without them, so a project missing either cannot
  // be opened at all. A project with no building-area rows is exactly what this flow produced before
  // RTLCA-5781 — the settings page opens with the fields blank — so an environment where the
  // permission is not deployed goes back to that rather than failing a create that would otherwise
  // have succeeded. Reported once per session by the same memo the settings save uses; any OTHER
  // failure is a real one and still fails the step.
  const buildingAreas: ProjectBuildingAreasWriteInput = {
    projectId,
    areaAboveGround: referencedAreas.areaAboveGround,
    floorsAboveGround: referencedAreas.floorsAboveGround,
    heatedAreaAboveGround: heatArea?.heatedFloorAreaAboveGround ?? null,
    areaBelowGround: referencedAreas.areaBelowGround,
    basementFloors: referencedAreas.basementFloors,
    heatedBasementArea: heatArea?.heatedBasementArea ?? null,
    buildingTypeId:
      project.buildingTypeEnum === null || project.buildingTypeEnum === undefined ? null : Number(project.buildingTypeEnum),
    uses: buildingUsesToSave(project),
  };

  if (hasAnyBuildingArea(buildingAreas) && !isRailUnavailable("projectBuildingAreas")) {
    try {
      await replaceProjectBuildingAreas(buildingAreas, { previous: null, labelFor: buildingTypeLabel });
    } catch (writeError: unknown) {
      if (!(writeError instanceof ProjectWriteUnavailableError)) throw writeError;
      noteRailUnavailable("projectBuildingAreas", writeError);
    }
  }
};

const buildingTypeLabel = (buildingTypeId: number): string => {
  const entry = useBuildingTypes()
    .getBuildingTypes()
    .find((option) => option.id === String(buildingTypeId));
  return entry ? buildingTypeDisplayLabel(entry.label) : String(buildingTypeId);
};
